import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Card, Modal, Spinner } from 'react-bootstrap'
import { FaMapMarkerAlt } from 'react-icons/fa'
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const dash = (v) =>
  v === null || v === undefined || String(v).trim() === '' ? '—' : String(v)

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const reverseGeocodeCache = new Map()

// Fix default Leaflet marker icons for bundlers (CRA/Webpack).
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

function RecenterMap({ center, zoom }) {
  const map = useMap()
  useEffect(() => {
    if (!center) return
    map.setView(center, zoom, { animate: true })
  }, [center, map, zoom])
  return null
}

const popupCss = `
.vehicle-tracking-popup.leaflet-popup .leaflet-popup-content-wrapper {
  padding: 6px 8px;
  border-radius: 10px;
}
.vehicle-tracking-popup.leaflet-popup .leaflet-popup-content {
  margin: 0;
  font-size: 11px;
  line-height: 1.2;
}
`

function getReverseGeocodeKey(lat, long) {
  const latKey = Number(lat).toFixed(5)
  const longKey = Number(long).toFixed(5)
  return `${latKey},${longKey}`
}

function useReverseGeocodedAddress(coords) {
  const [state, setState] = useState({
    loading: false,
    address: null,
    error: null,
  })

  const abortRef = useRef(null)
  const debounceRef = useRef(null)
  const lastRequestAtRef = useRef(0)

  const key = useMemo(() => {
    if (coords == null) return null
    if (!Number.isFinite(coords.lat) || !Number.isFinite(coords.long)) return null
    return getReverseGeocodeKey(coords.lat, coords.long)
  }, [coords?.lat, coords?.long])

  useEffect(() => {
    if (!key) {
      setState({ loading: false, address: null, error: null })
      return
    }
  
    // ✅ Check cache first
    const cached = reverseGeocodeCache.get(key)
    if (cached) {
      setState({ loading: false, address: cached, error: null })
      return
    }
  
    // Cleanup previous calls
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (abortRef.current) abortRef.current.abort()
  
    setState((prev) => ({ ...prev, loading: true, error: null }))
  
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController()
      abortRef.current = controller
  
      try {
        // ✅ Rate limit protection (IMPORTANT for Nominatim)
        const now = Date.now()
        const since = now - lastRequestAtRef.current
        if (since < 1100) {
          await new Promise((r) => setTimeout(r, 1100 - since))
        }
        lastRequestAtRef.current = Date.now()
  
        const lat = Number(coords.lat)
        const lon = Number(coords.long)
  
        const url = new URL('https://nominatim.openstreetmap.org/reverse')
        url.searchParams.set('lat', lat)
        url.searchParams.set('lon', lon)
        url.searchParams.set('format', 'json')
        url.searchParams.set('addressdetails', '1')
  
        const res = await fetch(url.toString(), {
          signal: controller.signal,
          headers: {
            Accept: 'application/json',
            'User-Agent': 'vehicle-tracking-app', // ✅ required by Nominatim
          },
        })
  
        if (!res.ok) {
          throw new Error(`Reverse geocode failed (${res.status})`)
        }
  
        const data = await res.json()
  
        // ✅ Option 1: simple full address
        let address = data?.display_name || null
  
        // ✅ Option 2 (better readable format)
        if (data?.address) {
          const a = data.address
          address = [
            a.road,
            a.suburb,
            a.city || a.town || a.village,
            a.state,
            a.country,
          ]
            .filter(Boolean)
            .join(', ')
        }
  
        if (address) {
          reverseGeocodeCache.set(key, address)
        }
  
        setState({
          loading: false,
          address,
          error: address ? null : 'Address not found',
        })
      } catch (e) {
        if (e?.name === 'AbortError') return
  
        setState({
          loading: false,
          address: null,
          error: e?.message || 'Unable to resolve address',
        })
      }
    }, 450)
  
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortRef.current) abortRef.current.abort()
    }
  }, [key])
  
  return state
}

function formatRelativeAgo(ms) {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

export function VehicleTrackingModal({
  show,
  onHide,
  busNumber,
  attendantName,
  busId,
  latest,
  locationLoading,
  canManageJourney,
  journeyActive,
  onStartOrUpdate,
  onComplete,
  onRefresh,
  inFlight,
}) {
  const coords = latest?.coords
  const noLocationMessage = latest?.noLocationMessage
  const hasCoords = coords != null && Number.isFinite(coords.lat) && Number.isFinite(coords.long)
  const addr = useReverseGeocodedAddress(hasCoords ? coords : null)
  const locationText = hasCoords
    ? addr.address || (addr.loading ? 'Resolving address…' : 'Address unavailable')
    : noLocationMessage || '—'
  const lastMs = latest?.updatedMs ?? null
  const err = latest?.error

  const mapCenter = hasCoords ? [Number(coords.lat), Number(coords.long)] : null

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header
        closeButton
        className="text-dark border-0"
        style={{
          background: 'linear-gradient(135deg, #BDE0FE 0%, #e0f2fe 100%)',
          paddingBottom: '12px',
        }}
      >
        <div className="d-flex align-items-center gap-3 w-100">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              backgroundColor: '#223843',
              color: '#fff',
            }}
          >
            <FaMapMarkerAlt size={22} />
          </div>
          <div className="text-start min-w-0">
            <Modal.Title
              className="h5 mb-0 fw-bold"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#0f172a' }}
            >
              {dash(busNumber)}
            </Modal.Title>
            <div
              className="text-truncate small mt-1"
              style={{ color: '#475569', maxWidth: '100%' }}
            >
              Attendant: {dash(attendantName)}
            </div>
          </div>
          <Badge
            bg={journeyActive ? 'success' : coords ? 'info' : 'secondary'}
            pill
            className="ms-auto flex-shrink-0 align-self-start"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            {journeyActive ? 'Journey live' : coords ? 'Last known' : 'No position'}
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-4 pb-2" style={{ backgroundColor: '#f8fafc' }}>
        <Card
          className="border-0 shadow-sm mb-4 overflow-hidden"
          style={{ borderRadius: '16px' }}
        >
          <div style={{ height: '280px', background: '#e2e8f0' }}>
            {hasCoords ? (
              <MapContainer
                center={mapCenter}
                zoom={16}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap center={mapCenter} zoom={16} />
                <Marker position={mapCenter}>
                  <Popup className="vehicle-tracking-popup" maxWidth={220} closeButton={false}>
                    <style>{popupCss}</style>
                    <div style={{ maxWidth: 220 }}>
                      <div className="fw-semibold" style={{ fontSize: '12px' }}>
                        {dash(busNumber)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{locationText}</div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                <div className="text-muted small">{noLocationMessage || 'No position yet'}</div>
              </div>
            )}
          </div>
        </Card>

        <div
          className="position-relative overflow-hidden rounded-4 mb-4 border"
          style={{
            borderColor: '#e2e8f0',
            background:
              'linear-gradient(160deg, #e0f2fe 0%, #f0f9ff 40%, #f8fafc 100%)',
            minHeight: '200px',
          }}
        >
          <div
            className="position-absolute opacity-25"
            style={{
              right: '-20px',
              top: '-20px',
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: '#38bdf8',
              filter: 'blur(40px)',
            }}
          />
          <div className="position-relative p-4 p-md-5">
            <p
              className="text-uppercase fw-semibold mb-2"
              style={{
                fontSize: '11px',
                letterSpacing: '0.08em',
                color: '#64748b',
              }}
            >
              Latest location
            </p>
            {locationLoading ? (
              <div className="d-flex align-items-center gap-2 py-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="text-muted small">Loading location…</span>
              </div>
            ) : (
              <>
                <p
                  className="mb-0 fw-semibold"
                  style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                    lineHeight: 1.5,
                    color: hasCoords ? '#0f172a' : '#475569',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {locationText}
                </p>
              </>
            )}
            {err ? <p className="text-danger small mt-2 mb-0">{err}</p> : null}
            {hasCoords && addr.error && !locationLoading ? (
              <p className="text-muted small mt-2 mb-0">{addr.error}</p>
            ) : null}
          </div>
        </div>

        <Card
          className="border-0 shadow-sm mb-3"
          style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
        >
          <Card.Body className="py-3 px-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
              <div>
                <div
                  className="fw-semibold mb-1"
                  style={{ fontSize: '13px', color: '#64748b' }}
                >
                  Last updated
                </div>
                <div style={{ fontSize: '15px', color: '#0f172a' }}>
                  {lastMs == null ? (
                    <span className="text-muted">
                      {noLocationMessage
                        ? 'No timestamp (awaiting first location share)'
                        : 'No server position yet'}
                    </span>
                  ) : (
                    <>
                      {new Date(lastMs).toLocaleString()}
                      <span className="text-muted ms-2" style={{ fontSize: '13px' }}>
                        ({formatRelativeAgo(lastMs)})
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div
                className="text-end small text-muted"
                style={{ maxWidth: '280px', fontSize: '12px' }}
              >
                {canManageJourney
                  ? 'Start journey sends your current GPS. While live, you can send updates anytime; this screen also refetches the last saved position periodically while open.'
                  : 'Location is updated when the assigned bus attendant shares GPS during an active journey. Last saved position refetches periodically while this window is open.'}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer
        className="border-0 pt-0 gap-2 flex-wrap"
        style={{ backgroundColor: '#f8fafc' }}
      >
        <Button variant="outline-secondary" onClick={onHide} className="px-4">
          Close
        </Button>
        <Button
          variant="outline-primary"
          className="px-3"
          disabled={!busId || inFlight?.[Number(busId)]}
          onClick={() => onRefresh(busId)}
        >
          Refresh location
        </Button>
        {canManageJourney ? (
          <>
            <Button
              variant="success"
              className="px-3 fw-semibold"
              disabled={!busId || inFlight?.[Number(busId)] === 'start'}
              onClick={() => onStartOrUpdate(busId)}
            >
              {inFlight?.[Number(busId)] === 'start' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending…
                </>
              ) : journeyActive ? (
                'Send current location'
              ) : (
                'Start journey (GPS)'
              )}
            </Button>
            <Button
              variant="danger"
              className="px-3 fw-semibold"
              disabled={!busId || !journeyActive || inFlight?.[Number(busId)] === 'complete'}
              onClick={() => onComplete(busId)}
            >
              {inFlight?.[Number(busId)] === 'complete' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Completing…
                </>
              ) : (
                'Complete journey'
              )}
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}

export function TrackVehicleButton({
  busId,
  attendantName,
  busNumber,
  journeyActive,
  canManageJourney,
  inFlight,
  onOpenModal,
  label = 'Track vehicle',
}) {
  const live = Boolean(journeyActive)
  return (
    <div className="d-flex flex-column align-items-start gap-2">
      <Badge bg={live ? 'success' : 'secondary'} pill className="fw-normal">
        {live ? 'Journey live' : 'View location'}
      </Badge>
      <Button
        type="button"
        size="sm"
        className="fw-semibold d-inline-flex align-items-center gap-1"
        style={{
          fontSize: '12px',
          borderRadius: '8px',
          backgroundColor: '#223843',
          border: 'none',
          color: '#fff',
        }}
        disabled={Boolean(inFlight?.[Number(busId)]) || busId == null}
        title={busId == null ? 'No bus is linked for tracking.' : undefined}
        onClick={() =>
          onOpenModal({
            busId,
            attendantName,
            busNumber,
            canManageJourney,
          })
        }
      >
        <FaMapMarkerAlt size={14} />
        {label}
      </Button>
    </div>
  )
}

